import type { BaseRow } from '@openx/types/tableHelpers.ts';
import { STICKY_POSITION } from '@openx/types/tableHelpers.ts';
import { useTranslation } from 'react-i18next';
import { DEAL_INSIGHTS_COLUMN_MIN_WIDTH } from '../../../../HeatTable/constants.ts';
import { useHeatmapTableColumns } from '../../../../HeatTable/hooks/useHeatmapTableColumns.tsx';
import type { ColorConfig, ColumnConfig } from '../../../../HeatTable/types.ts';

export interface DealInsightsData extends BaseRow {
  mobileappname?: string;
  domain_bundle?: string;
  spend: string;
  spend_raw: number | null;
  sov: string;
  sov_raw: number | null;
  ecpm: string;
  ecpm_raw: number | null;
  vcr: string;
  vcr_raw: number | null;
  ctr: string;
  ctr_raw: number | null;
  viewability: string;
  viewability_raw: number | null;
  impressions: string;
  impressions_raw: number | null;
}

interface MaxValuesConfig {
  spend: ColorConfig<DealInsightsData>;
  sov: ColorConfig<DealInsightsData>;
  ecpm: ColorConfig<DealInsightsData>;
  vcr: ColorConfig<DealInsightsData>;
  ctr: ColorConfig<DealInsightsData>;
  viewability: ColorConfig<DealInsightsData>;
  impressions: ColorConfig<DealInsightsData>;
}

export const useDeliveryByDomainBundleColumns = (maxValues: MaxValuesConfig) => {
  const { t } = useTranslation();

  const columnsConfig: ColumnConfig<DealInsightsData>[] = [
    {
      key: 'domain_bundle',
      title: t('Domain/Bundle'),
      width: '20%',
      minWidth: DEAL_INSIGHTS_COLUMN_MIN_WIDTH.DOMAIN_BUNDLE,
      sticky: STICKY_POSITION.LEFT,
      disabled: true,
      freeze: true,
      cellStyle: {
        alignItems: 'flex-start',
        fontWeight: 'fontWeightBold',
        paddingLeft: 0,
        paddingTop: theme => theme.spacing(2),
        paddingBottom: theme => theme.spacing(2),
      },
      headerStyle: {
        paddingLeft: 4,
      },
    },
    {
      key: 'mobileappname',
      title: t('App Name'),
      width: '10%',
      minWidth: DEAL_INSIGHTS_COLUMN_MIN_WIDTH.APP_NAME,
      sticky: STICKY_POSITION.LEFT,
      disabled: true,
      freeze: true,
      cellStyle: {
        alignItems: 'flex-start',
        paddingLeft: 0,
      },
    },
    {
      key: 'impressions',
      title: t('Impressions'),
      width: '5%',
      minWidth: DEAL_INSIGHTS_COLUMN_MIN_WIDTH.METRIC,
      colorConfig: {
        ...maxValues.impressions,
        valueKey: 'impressions_raw',
      },
      headerStyle: {
        '& > div': {
          justifyContent: 'center',
        },
      },
    },
    {
      key: 'spend',
      title: t('Spend'),
      width: '5%',
      minWidth: DEAL_INSIGHTS_COLUMN_MIN_WIDTH.METRIC,
      prefix: '$',
      colorConfig: {
        ...maxValues.spend,
        valueKey: 'spend_raw',
      },
      headerStyle: {
        '& > div': {
          justifyContent: 'center',
        },
      },
    },
    {
      key: 'sov',
      title: t('SOV'),
      width: '5%',
      minWidth: DEAL_INSIGHTS_COLUMN_MIN_WIDTH.METRIC,
      suffix: '%',
      colorConfig: {
        ...maxValues.sov,
        valueKey: 'sov_raw',
      },
      headerStyle: {
        '& > div': {
          justifyContent: 'center',
        },
      },
    },
    {
      key: 'ecpm',
      title: t('eCPM'),
      width: '5%',
      minWidth: DEAL_INSIGHTS_COLUMN_MIN_WIDTH.METRIC,
      prefix: '$',
      colorConfig: {
        ...maxValues.ecpm,
        valueKey: 'ecpm_raw',
      },
      headerStyle: {
        '& > div': {
          justifyContent: 'center',
        },
      },
    },
    {
      key: 'viewability',
      title: t('Viewability'),
      width: '5%',
      minWidth: DEAL_INSIGHTS_COLUMN_MIN_WIDTH.METRIC,
      suffix: '%',
      colorConfig: {
        ...maxValues.viewability,
        valueKey: 'viewability_raw',
      },
      headerStyle: {
        '& > div': {
          justifyContent: 'center',
        },
      },
    },
    {
      key: 'vcr',
      title: t('VCR'),
      width: '5%',
      minWidth: DEAL_INSIGHTS_COLUMN_MIN_WIDTH.METRIC,
      suffix: '%',
      colorConfig: {
        ...maxValues.vcr,
        valueKey: 'vcr_raw',
      },
      headerStyle: {
        '& > div': {
          justifyContent: 'center',
        },
      },
    },
    {
      key: 'ctr',
      title: t('CTR'),
      width: '5%',
      minWidth: DEAL_INSIGHTS_COLUMN_MIN_WIDTH.METRIC,
      suffix: '%',
      colorConfig: {
        ...maxValues.ctr,
        valueKey: 'ctr_raw',
      },
      headerStyle: {
        '& > div': {
          justifyContent: 'center',
        },
      },
    },
  ];

  return useHeatmapTableColumns(columnsConfig);
};
